'use client'

import { useCallback } from 'react'
import { useGetDatabaseOrganizationQuery } from '@/api/organization/get-database-organization'
import { useEnsureReferralCodeMutation } from '@/api/organization/ensure-referral-code-mutation'
import { useCopyToClipboard } from '@/utils/hooks/use-copy-to-clipboard'
import { trackEvent, TrackEventName } from '@/utils/tracking/track'
import { toast } from '@/features/toast/toast'

const REFERRAL_BASE_URL = 'https://superscale.ai'

export function useReferralLink() {
    const { data: organization } = useGetDatabaseOrganizationQuery()
    const ensureCodeMutation = useEnsureReferralCodeMutation()
    const { copyToClipboard } = useCopyToClipboard({
        onSuccess: () => toast.success('Referral link copied!'),
    })

    const referralCode = organization?.referralCode ?? null
    const referralLink = referralCode ? `${REFERRAL_BASE_URL}?r=${referralCode}` : null

    const copyReferralLink = useCallback(
        async (source: 'banner' | 'settings') => {
            let code = referralCode
            if (!code) {
                const result = await ensureCodeMutation.mutateAsync()
                code = result.referralCode
            }

            const link = `${REFERRAL_BASE_URL}?r=${code}`
            await copyToClipboard(link)
            trackEvent(TrackEventName.ReferralLinkCopied, { referral_code: code, source })
        },
        [referralCode, ensureCodeMutation, copyToClipboard]
    )

    const ensureReferralCode = useCallback(async () => {
        if (referralCode) return
        await ensureCodeMutation.mutateAsync()
    }, [referralCode, ensureCodeMutation])

    return {
        referralCode,
        referralLink,
        copyReferralLink,
        ensureReferralCode,
        isGenerating: ensureCodeMutation.isPending,
    }
}
